/**
 * A wireline unit's crew templates (GET /wireline-slickline/units/:id → crews),
 * on the unit's own page.
 *
 * A crew is a named crew on one unit, and its members are the hands who
 * usually work it, each in a position (supervisor / engineer, operator,
 * helper — the API's catalog), ordered by `sort_order` then name. WL-1 stores
 * the template only; a job's crew is copied from it later.
 *
 * WL_CREW_WRITE (admin, office_staff, supervisor, dispatcher) adds, edits and
 * removes crews and members; every other role reads. Each refusal reads as a
 * sentence in the dialog, never as a green toast: 409 duplicate_crew_name,
 * 409 already_on_crew, and a 400 for a terminated hand or a customer login.
 * The person picker leaves out anyone already on that crew.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useAddWlCrewMember,
  useCreateWlCrew,
  useDeleteWlCrew,
  useRemoveWlCrewMember,
  useUpdateWlCrew,
  useUpdateWlCrewMember,
  useWlUsers,
} from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { wlBlankToNull, wlCatalogLabel, wlRefusal } from '../../lib/wireline-slickline';
import type { WlCatalog, WlCrew, WlCrewMember, WlUnitDetail } from '../../types/wireline-slickline';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WL_SMALL_BTN, WlDialog, WlEmpty, WlField, WlFormError, WlPanel } from './WlBits';

type Dialog =
  | { kind: 'crew'; crew: WlCrew | null }
  | { kind: 'deleteCrew'; crew: WlCrew }
  | { kind: 'member'; crew: WlCrew; member: WlCrewMember | null }
  | { kind: 'removeMember'; crew: WlCrew; member: WlCrewMember }
  | null;

const SMALL_BTN = WL_SMALL_BTN;

function CrewDialog({ unit, crew, onClose }: { unit: WlUnitDetail; crew: WlCrew | null; onClose: () => void }) {
  const create = useCreateWlCrew();
  const update = useUpdateWlCrew();
  const [name, setName] = useState(crew?.name ?? '');
  const [notes, setNotes] = useState(crew?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;

  const save = () => {
    setError(null);
    if (!name.trim()) return setError('Give the crew a name, e.g. A crew.');
    const done = { onSuccess: () => { toast.success(crew ? 'Crew saved' : 'Crew added'); onClose(); }, onError: (err: unknown) => setError(wlRefusal(err, 'The crew could not be saved.')) };
    if (!crew) return create.mutate({ unitId: unit.id, body: { name: name.trim(), notes: wlBlankToNull(notes) } }, done);
    const body: Record<string, unknown> = {};
    if (name.trim() !== crew.name) body.name = name.trim();
    if (wlBlankToNull(notes) !== (crew.notes ?? null)) body.notes = wlBlankToNull(notes);
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    update.mutate({ id: crew.id, body }, done);
  };

  return (
    <WlDialog
      open
      title={crew ? `Edit ${crew.name}` : `Add a crew to ${unit.unitNumber}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WL_SECONDARY} onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className={WL_PRIMARY} onClick={save} disabled={pending}>
            {pending ? 'Saving…' : crew ? 'Save crew' : 'Add crew'}
          </button>
        </>
      }
    >
      <div className="space-y-3" data-testid="wl-crew-dialog">
        <WlFormError message={error} />
        <WlField label="Crew name">
          <input className={WL_INPUT} maxLength={100} value={name} onChange={(e) => setName(e.target.value)} aria-label="Crew name" />
        </WlField>
        <WlField label="Notes">
          <textarea className={`${WL_INPUT} h-20 py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Crew notes" />
        </WlField>
      </div>
    </WlDialog>
  );
}

function MemberDialog({
  crew,
  member,
  catalog,
  onClose,
}: {
  crew: WlCrew;
  member: WlCrewMember | null;
  catalog: WlCatalog | null;
  onClose: () => void;
}) {
  const users = useWlUsers(!member);
  const add = useAddWlCrewMember();
  const update = useUpdateWlCrewMember();
  const [userId, setUserId] = useState('');
  const [position, setPosition] = useState(member?.position ?? '');
  const [order, setOrder] = useState(member ? String(member.sortOrder) : '');
  const [error, setError] = useState<string | null>(null);
  const pending = add.isPending || update.isPending;
  const onCrew = new Set(crew.members.map((m) => m.userId));
  const candidates = (users.data ?? []).filter((u) => !onCrew.has(u.id));
  const usersFailure = queryLoadFailure(users);
  // A new hand goes to the bottom of the crew's order.
  const nextOrder = crew.members.reduce((n, m) => Math.max(n, m.sortOrder ?? 0), 0) + 10;

  const save = () => {
    setError(null);
    if (!member && !userId) return setError('Pick the person to add.');
    if (!position) return setError('Pick their position on the crew.');
    const done = { onSuccess: () => { toast.success(member ? 'Crew member saved' : 'Added to the crew'); onClose(); }, onError: (err: unknown) => setError(wlRefusal(err, 'The crew member could not be saved.')) };
    if (!member) return add.mutate({ crewId: crew.id, body: { user_id: userId, position, sort_order: Math.min(nextOrder, 100000) } }, done);
    const body: Record<string, unknown> = {};
    if (position !== member.position) body.position = position;
    if (order.trim() !== String(member.sortOrder)) {
      const n = Number(order.trim());
      if (!/^\d+$/.test(order.trim()) || n > 100000) return setError('Order must be a whole number from 0 to 100000.');
      body.sort_order = n;
    }
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    update.mutate({ id: member.id, body }, done);
  };

  return (
    <WlDialog
      open
      title={member ? `Edit ${member.userName ?? 'crew member'}` : `Add to ${crew.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WL_SECONDARY} onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className={WL_PRIMARY} onClick={save} disabled={pending || (!member && !!usersFailure)}>
            {pending ? 'Saving…' : member ? 'Save' : 'Add to crew'}
          </button>
        </>
      }
    >
      <div className="space-y-3" data-testid="wl-member-dialog">
        <WlFormError message={error} />
        {!member &&
          (users.isLoading ? (
            <p className="text-sm text-fieldiq-muted">Loading people…</p>
          ) : usersFailure ? (
            <LoadFailureNotice error={queryFailureError(users)} kind={usersFailure} subject="your people" />
          ) : (
            <WlField label="Person" hint={candidates.length === 0 ? 'Everyone is already on this crew.' : undefined}>
              <select className={WL_INPUT} value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="Person">
                <option value="">Select a person…</option>
                {candidates.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </WlField>
          ))}
        <WlField label="Position">
          <select className={WL_INPUT} value={position} onChange={(e) => setPosition(e.target.value)} aria-label="Position">
            <option value="">Select a position…</option>
            {(catalog?.crewPositions ?? []).map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </WlField>
        {member && (
          <WlField label="Order" hint="Lower comes first on the crew list.">
            <input className={WL_INPUT} inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value)} aria-label="Order" />
          </WlField>
        )}
      </div>
    </WlDialog>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  keepLabel,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  keepLabel: string;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <WlDialog
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WL_SECONDARY} onClick={onClose} disabled={pending}>
            {keepLabel}
          </button>
          <button type="button" className={WL_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <WlFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </WlDialog>
  );
}

export function UnitCrews({ unit, catalog, canWrite }: { unit: WlUnitDetail; catalog: WlCatalog | null; canWrite: boolean }) {
  const [dialog, setDialog] = useState<Dialog>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const delCrew = useDeleteWlCrew();
  const removeMember = useRemoveWlCrewMember();
  const crews = unit.crews ?? [];
  const close = () => {
    setDialog(null);
    setConfirmError(null);
  };

  return (
    <WlPanel
      eyebrow="Crew templates"
      title="Crews"
      testId="wl-unit-crews"
      actions={
        canWrite ? (
          <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'crew', crew: null })}>
            <Plus className="size-3.5" aria-hidden="true" />
            Add crew
          </button>
        ) : undefined
      }
    >
      {crews.length === 0 ? (
        <WlEmpty>This unit has no crews yet.</WlEmpty>
      ) : (
        <div className="divide-y divide-fieldiq-border">
          {crews.map((crew) => (
            <div key={crew.id} className="space-y-2 px-4 py-4" data-testid="wl-crew">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-extrabold text-white">{crew.name}</p>
                  <p className="text-xs text-fieldiq-muted">
                    {crew.members.length} hand{crew.members.length === 1 ? '' : 's'}
                  </p>
                  {crew.notes && <p className="mt-1 text-sm text-fieldiq-text">{crew.notes}</p>}
                </div>
                {canWrite && (
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'member', crew, member: null })}>
                      <UserPlus className="size-3.5" aria-hidden="true" />
                      Add hand
                    </button>
                    <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'crew', crew })} aria-label={`Edit ${crew.name}`}>
                      <Pencil className="size-3.5" aria-hidden="true" />
                      Edit
                    </button>
                    <button type="button" className={SMALL_BTN} onClick={() => setDialog({ kind: 'deleteCrew', crew })} aria-label={`Delete ${crew.name}`}>
                      <Trash2 className="size-3.5" aria-hidden="true" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
              {crew.members.length === 0 ? (
                <p className="text-sm text-fieldiq-muted">Nobody is on this crew yet.</p>
              ) : (
                <ul className="divide-y divide-fieldiq-border/60 rounded-fi-control border border-fieldiq-border">
                  {crew.members.map((m) => (
                    <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid="wl-crew-member">
                      <div className="min-w-0">
                        <span className="text-sm font-semibold text-white">{m.userName || '—'}</span>
                        <span className="ml-2 text-xs text-fieldiq-muted">{m.positionLabel ?? wlCatalogLabel(catalog?.crewPositions, m.position)}</span>
                        {m.employmentStatus === 'terminated' && (
                          <span className="ml-2 text-xs font-semibold text-fi-signal-red">No longer employed</span>
                        )}
                      </div>
                      {canWrite && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            className={SMALL_BTN}
                            onClick={() => setDialog({ kind: 'member', crew, member: m })}
                            aria-label={`Edit ${m.userName ?? 'crew member'}`}
                          >
                            <Pencil className="size-3.5" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={SMALL_BTN}
                            onClick={() => setDialog({ kind: 'removeMember', crew, member: m })}
                            aria-label={`Remove ${m.userName ?? 'crew member'}`}
                          >
                            <UserMinus className="size-3.5" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {dialog?.kind === 'crew' && <CrewDialog unit={unit} crew={dialog.crew} onClose={close} />}
      {dialog?.kind === 'member' && <MemberDialog crew={dialog.crew} member={dialog.member} catalog={catalog} onClose={close} />}
      {dialog?.kind === 'deleteCrew' && (
        <ConfirmDialog
          title={`Delete ${dialog.crew.name}`}
          body={`Deletes the crew and takes its ${dialog.crew.members.length} hand${dialog.crew.members.length === 1 ? '' : 's'} off it. Nobody is removed from the company.`}
          confirmLabel="Delete crew"
          keepLabel="Keep crew"
          pending={delCrew.isPending}
          error={confirmError}
          onClose={close}
          onConfirm={() =>
            delCrew.mutate(dialog.crew.id, {
              onSuccess: () => {
                toast.success('Crew deleted');
                close();
              },
              onError: (err) => setConfirmError(wlRefusal(err, 'The crew could not be deleted.')),
            })
          }
        />
      )}
      {dialog?.kind === 'removeMember' && (
        <ConfirmDialog
          title={`Remove ${dialog.member.userName ?? 'crew member'}`}
          body={`Takes ${dialog.member.userName ?? 'this person'} off ${dialog.crew.name}. They stay in the company.`}
          confirmLabel="Remove"
          keepLabel="Keep"
          pending={removeMember.isPending}
          error={confirmError}
          onClose={close}
          onConfirm={() =>
            removeMember.mutate(dialog.member.id, {
              onSuccess: () => {
                toast.success('Removed from the crew');
                close();
              },
              onError: (err) => setConfirmError(wlRefusal(err, 'The crew member could not be removed.')),
            })
          }
        />
      )}
    </WlPanel>
  );
}
